import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Wind, ArrowLeft, Play, Pause, RotateCcw, Calendar, ChevronRight, Heart, Crown } from "lucide-react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const BreathingPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [exercises, setExercises] = useState([]);
  const [dailyExercise, setDailyExercise] = useState(null);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [phase, setPhase] = useState("ready"); // ready, inhale, hold, exhale
  const [cycleCount, setCycleCount] = useState(0);
  const [timer, setTimer] = useState(0);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    fetchExercises();
    fetchFavorites();
    fetchPremiumStatus();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const fetchFavorites = async () => {
    try {
      const res = await axios.get(`${API}/favorites/breathwork`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(res.data.favorites || []);
    } catch (error) {
      console.error("Error fetching favorites:", error);
    }
  };

  const toggleFavorite = async (exerciseId, e) => {
    e?.stopPropagation();
    try {
      const res = await axios.post(`${API}/favorites/toggle`, {
        item_id: exerciseId,
        item_type: 'breathwork'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.is_favorited) {
        setFavorites([...favorites, exerciseId]);
        toast.success("Added to favorites");
      } else {
        setFavorites(favorites.filter(id => id !== exerciseId));
        toast.success("Removed from favorites");
      }
    } catch (error) {
      toast.error("Failed to update favorites");
    }
  };

  const fetchExercises = async () => {
    try {
      const [exercisesRes, dailyRes] = await Promise.all([
        axios.get(`${API}/content/breathing`),
        axios.get(`${API}/content/daily`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setExercises(exercisesRes.data.exercises);
      setDailyExercise(dailyRes.data.breathing);
    } catch (error) {
      console.error("Error fetching exercises:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPremiumStatus = async () => {
    try {
      const res = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsPremium(res.data.is_premium || false);
    } catch (error) {
      console.error("Error fetching premium status:", error);
    }
  };

  const handleSelectExercise = (exercise) => {
    if (exercise.is_premium && !isPremium) {
      toast.error("This breathwork practice requires a Premium subscription");
      navigate("/subscription");
      return;
    }
    setSelectedExercise(exercise);
    setPhase("ready");
    setCycleCount(0);
    setIsActive(false);
    setTimer(0);
  };

  const recordBreathworkCompletion = async () => {
    if (!selectedExercise) return;
    try {
      await axios.post(`${API}/content/breathing/${selectedExercise.id}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Breathwork complete! Your garden is growing 🌱");
    } catch (e) {
      console.error("Failed to record breathwork completion:", e);
    }
  };

  const startBreathing = () => {
    setIsActive(true);
    setPhase("inhale");
    setTimer(selectedExercise.inhale_duration);
    runBreathingCycle();
  };

  const runBreathingCycle = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    
    let currentPhase = "inhale";
    let currentTimer = selectedExercise.inhale_duration;
    let currentCycle = 0;
    
    setPhase("inhale");
    setTimer(selectedExercise.inhale_duration);
    
    intervalRef.current = setInterval(() => {
      currentTimer--;
      setTimer(currentTimer);
      
      if (currentTimer <= 0) {
        if (currentPhase === "inhale") {
          if (selectedExercise.hold_duration > 0) {
            currentPhase = "hold";
            currentTimer = selectedExercise.hold_duration;
          } else {
            currentPhase = "exhale";
            currentTimer = selectedExercise.exhale_duration;
          }
        } else if (currentPhase === "hold") {
          currentPhase = "exhale";
          currentTimer = selectedExercise.exhale_duration;
        } else if (currentPhase === "exhale") {
          currentCycle++;
          setCycleCount(currentCycle);
          
          if (currentCycle >= selectedExercise.cycles) {
            clearInterval(intervalRef.current);
            setPhase("complete");
            setIsActive(false);
            // Record completion and grow garden
            recordBreathworkCompletion();
            return;
          }
          
          currentPhase = "inhale";
          currentTimer = selectedExercise.inhale_duration;
        }
        
        setPhase(currentPhase);
        setTimer(currentTimer);
      }
    }, 1000);
  };

  const pauseBreathing = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setIsActive(false);
  };

  const resetBreathing = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setPhase("ready");
    setCycleCount(0);
    setIsActive(false);
    setTimer(0);
  };

  const getCircleScale = () => {
    if (phase === "inhale") return "scale-125";
    if (phase === "hold") return "scale-125";
    if (phase === "exhale") return "scale-100";
    return "scale-100";
  };

  const getPhaseText = () => {
    if (phase === "ready") return "Ready to begin";
    if (phase === "inhale") return "Breathe In";
    if (phase === "hold") return "Hold";
    if (phase === "exhale") return "Breathe Out";
    if (phase === "complete") return "Well done, beautiful soul";
    return "";
  };

  if (loading) {
    return (
      <div className="dark min-h-screen bg-stone-950 flex items-center justify-center">
        <div className="animate-breathe">
          <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center">
            <Wind className="w-8 h-8 text-accent" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dark min-h-screen bg-stone-950" data-testid="breathing-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => selectedExercise ? setSelectedExercise(null) : navigate("/dashboard")}
            className="text-muted-foreground hover:text-foreground"
            data-testid="breathing-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {selectedExercise ? "All Exercises" : "Dashboard"}
          </Button>
          <div className="flex items-center gap-2">
            <Wind className="w-5 h-5 text-accent" strokeWidth={1.5} />
            <span className="font-heading text-lg font-semibold">Breathing</span>
          </div>
          <div className="w-24"></div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* List View */}
        {!selectedExercise && (
          <div className="animate-fade-in">
            <div className="text-center mb-10">
              <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mb-3">
                30 Breathing Exercises
              </h1>
              <p className="text-muted-foreground text-lg max-w-xl mx-auto">
                Simple practices to calm your nervous system and anchor you in the present
              </p>
            </div>

            {/* Today's Exercise */}
            {dailyExercise && (
              <Card 
                className="glass-card rounded-3xl border-0 mb-10 cursor-pointer card-hover"
                onClick={() => handleSelectExercise(dailyExercise)}
                data-testid="daily-breathing-feature"
              >
                <CardContent className="p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Calendar className="w-4 h-4 text-accent" />
                    <span className="text-xs text-accent uppercase tracking-wide">Today's Breathwork</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-2xl bg-accent/20 flex items-center justify-center">
                        <Wind className="w-8 h-8 text-accent" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h2 className="font-heading text-xl font-semibold text-foreground">
                          {dailyExercise.title}
                        </h2>
                        <p className="text-sm text-muted-foreground mb-1">
                          {dailyExercise.description}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-accent">
                          <span>{dailyExercise.inhale_duration}-{dailyExercise.hold_duration}-{dailyExercise.exhale_duration}</span>
                          <span>×{dailyExercise.cycles} cycles</span>
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-6 h-6 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* All Exercises */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-lg font-semibold text-foreground">All Exercises</h2>
              <Button
                variant={showFavoritesOnly ? "default" : "outline"}
                size="sm"
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                className="rounded-full"
              >
                <Heart className={`w-4 h-4 mr-1 ${showFavoritesOnly ? 'fill-current' : ''}`} />
                {showFavoritesOnly ? 'Show All' : `Favorites (${favorites.length})`}
              </Button>
            </div>
            
            <div className="grid gap-3">
              {(showFavoritesOnly ? exercises.filter(e => favorites.includes(e.id)) : exercises).map((exercise) => (
                <Card
                  key={exercise.id}
                  className={`glass-card rounded-2xl border-0 cursor-pointer card-hover ${exercise.is_premium && !isPremium ? 'opacity-70' : ''}`}
                  onClick={() => handleSelectExercise(exercise)}
                  data-testid={`breathing-${exercise.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${exercise.is_premium && !isPremium ? 'bg-muted' : 'bg-accent/10'}`}>
                          {exercise.is_premium && !isPremium ? (
                            <Crown className="w-6 h-6 text-amber-500" strokeWidth={1.5} />
                          ) : (
                            <Wind className="w-6 h-6 text-accent" strokeWidth={1.5} />
                          )}
                        </div>
                        <div>
                          <h3 className="font-heading text-base font-semibold text-foreground">
                            {exercise.title}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {exercise.description}
                          </p>
                          <div className="flex items-center gap-3 text-xs text-accent mt-1">
                            <span>{exercise.inhale_duration}-{exercise.hold_duration}-{exercise.exhale_duration}</span>
                            <span>×{exercise.cycles} cycles</span>
                            {exercise.is_premium && !isPremium && (
                              <span className="text-amber-600 font-medium">Premium</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => toggleFavorite(exercise.id, e)}
                          className="p-2 rounded-full hover:bg-accent/10 transition-colors"
                          data-testid={`favorite-breathing-${exercise.id}`}
                        >
                          <Heart 
                            className={`w-5 h-5 ${favorites.includes(exercise.id) ? 'text-red-500 fill-red-500' : 'text-muted-foreground'}`} 
                          />
                        </button>
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {showFavoritesOnly && favorites.length === 0 && (
              <Card className="glass-card rounded-2xl border-0 mt-4">
                <CardContent className="p-8 text-center">
                  <Heart className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading text-lg font-semibold text-foreground mb-2">No favorites yet</h3>
                  <p className="text-muted-foreground">Tap the heart icon on any breathing exercise to add it to your favorites.</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Exercise View */}
        {selectedExercise && (
          <div className="animate-fade-in">
            <div className="text-center mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground mb-2">
                {selectedExercise.title}
              </h1>
              <p className="text-muted-foreground">
                {selectedExercise.description}
              </p>
              <div className="flex items-center justify-center gap-3 text-sm text-accent mt-2">
                <span>Inhale {selectedExercise.inhale_duration}s</span>
                {selectedExercise.hold_duration > 0 && <span>Hold {selectedExercise.hold_duration}s</span>}
                <span>Exhale {selectedExercise.exhale_duration}s</span>
                <span>×{selectedExercise.cycles}</span>
              </div>
            </div>

            {/* Breathing Circle */}
            <div className="flex flex-col items-center justify-center py-12">
              <div 
                className={`w-48 h-48 md:w-64 md:h-64 rounded-full bg-gradient-to-br from-primary/30 to-accent/30 flex items-center justify-center transition-transform duration-1000 ease-in-out ${getCircleScale()}`}
                data-testid="breathing-circle"
              >
                <div className="w-3/4 h-3/4 rounded-full bg-gradient-to-br from-primary/50 to-accent/40 flex items-center justify-center">
                  <div className="text-center">
                    {phase !== "ready" && phase !== "complete" && (
                      <span className="text-4xl font-heading text-foreground" data-testid="breathing-timer">
                        {timer}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Phase Text */}
              <p className="font-heading text-xl text-foreground mt-8" data-testid="breathing-phase">
                {getPhaseText()}
              </p>

              {/* Cycle Counter */}
              {(isActive || phase === "complete") && (
                <p className="text-sm text-muted-foreground mt-2">
                  Cycle {cycleCount} of {selectedExercise.cycles}
                </p>
              )}

              {/* Affirmation */}
              <p className="font-handwritten text-lg text-primary mt-6 max-w-md text-center" data-testid="breathing-affirmation">
                {selectedExercise.affirmation}
              </p>
            </div>

            {/* Controls */}
            <div className="flex justify-center gap-4">
              {phase === "ready" && (
                <Button
                  onClick={startBreathing}
                  className="btn-primary"
                  data-testid="start-breathing-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Begin
                </Button>
              )}

              {isActive && (
                <Button
                  onClick={pauseBreathing}
                  className="btn-secondary"
                  data-testid="pause-breathing-btn"
                >
                  <Pause className="w-4 h-4 mr-2" />
                  Pause
                </Button>
              )}

              {(phase !== "ready" && !isActive) && phase !== "complete" && (
                <>
                  <Button
                    onClick={startBreathing}
                    className="btn-primary"
                    data-testid="resume-breathing-btn"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Resume
                  </Button>
                  <Button
                    onClick={resetBreathing}
                    variant="ghost"
                    className="text-muted-foreground"
                    data-testid="reset-breathing-btn"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reset
                  </Button>
                </>
              )}

              {phase === "complete" && (
                <Button
                  onClick={resetBreathing}
                  className="btn-primary"
                  data-testid="restart-breathing-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Practice Again
                </Button>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default BreathingPage;
